"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useQuery } from "convex/react";
import type { Route } from "next";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { MonthlyProjectsChart, MonthlyProjectsChartSkeleton, OpenWorkflowChart, OpenWorkflowChartSkeleton } from "@/components/dashboard/project-charts";
import { Section } from "@/components/ui/section";
import { StatGrid, StatTile, StatValueSkeleton } from "@/components/ui/stat";
import { buildOrganizationPath } from "@/lib/organization-path";
interface Stat {
  label: string;
  value: ReactNode;
  hint?: string;
  /** Organization-relative list path the tile opens. */
  path?: string;
}

/** Placeholder for values that are not loaded, e.g. without an active subscription. */
const NO_VALUE = "-";

/** Section pair for the two project charts; side by side on wide screens. */
function ChartSections(t0) {
  const $ = _c(7);
  const {
    workflow,
    monthly
  } = t0;
  let t1;
  if ($[0] !== workflow) {
    t1 = <Section title="Andamento dos projetos abertos">{workflow}</Section>;
    $[0] = workflow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== monthly) {
    t2 = <Section title={"Novos projetos por m\xEAs"}>{monthly}</Section>;
    $[2] = monthly;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="grid gap-6 lg:grid-cols-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Organization dashboard summary: headline counts, the "Foco operacional" section and the
 * project charts. Renders siblings so the parent `SectionStack` separates them with a rule.
 * Loading keeps the loaded layout: real labels and hints with placeholder values.
 * `pending` renders that loading state without querying, before Convex auth is ready.
 */
export function DashboardOverview(t0) {
  const $ = _c(51);
  const {
    subscriptionLocked: t1,
    pending: t2
  } = t0;
  const subscriptionLocked = t1 === undefined ? false : t1;
  const pending = t2 === undefined ? false : t2;
  const {
    organizationSlug
  } = useParams();
  const skip = subscriptionLocked || pending;
  let t3;
  if ($[0] !== skip) {
    t3 = skip ? "skip" : {};
    $[0] = skip;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const producerStats = useQuery(api.producers.queries.getProducerStats, t3);
  let t4;
  if ($[2] !== skip) {
    t4 = skip ? "skip" : {};
    $[2] = skip;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const projectDashboard = useQuery(api.projects.queries.getProjectsDashboard, t4);
  const isLoading = pending || !subscriptionLocked && (producerStats === undefined || projectDashboard === undefined);
  let t5;
  if ($[4] !== isLoading || $[5] !== subscriptionLocked) {
    t5 = value => isLoading ? <StatValueSkeleton /> : subscriptionLocked ? NO_VALUE : value;
    $[4] = isLoading;
    $[5] = subscriptionLocked;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const show = t5;
  const totalProducers = producerStats?.total ?? 0;
  const activeProducers = producerStats?.active ?? 0;
  const totalProjects = projectDashboard?.totalProjects ?? 0;
  const openProjects = projectDashboard?.openProjects ?? 0;
  const completedProjects = projectDashboard?.completedProjects ?? 0;
  const canceledProjects = projectDashboard?.canceledProjects ?? 0;
  const recentlyCreatedProducers = producerStats?.recentActivity?.recentlyCreated ?? 0;
  const closedProjects = completedProjects + canceledProjects;
  let t6;
  if ($[7] !== closedProjects || $[8] !== completedProjects) {
    t6 = closedProjects > 0 ? `${Math.round(completedProjects / closedProjects * 100)}%` : NO_VALUE;
    $[7] = closedProjects;
    $[8] = completedProjects;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const completionRate = t6;
  let T0;
  let T1;
  let t10;
  let t7;
  let t8;
  let t9;
  let tileHref;
  if ($[10] !== activeProducers || $[11] !== canceledProjects || $[12] !== completedProjects || $[13] !== completionRate || $[14] !== isLoading || $[15] !== openProjects || $[16] !== organizationSlug || $[17] !== recentlyCreatedProducers || $[18] !== show || $[19] !== subscriptionLocked || $[20] !== totalProducers || $[21] !== totalProjects) {
    const stats = [{
      label: "Produtores ativos",
      value: show(activeProducers),
      hint: totalProducers !== activeProducers ? `de ${totalProducers} no total` : undefined,
      path: "/producers?status=active"
    }, {
      label: "Projetos abertos",
      value: show(openProjects),
      hint: totalProjects !== openProjects ? `de ${totalProjects} no total` : undefined,
      path: "/projects?status=open"
    }, {
      label: "Projetos conclu\xEDdos",
      value: show(completedProjects),
      path: "/projects?status=completed"
    }, {
      label: "Projetos cancelados",
      value: show(canceledProjects),
      path: "/projects?status=canceled"
    }];
    const focusItems = [{
      label: "Novos produtores",
      value: show(recentlyCreatedProducers),
      hint: "\xFAltimos 30 dias"
    }, {
      label: "Taxa de conclus\xE3o",
      value: show(completionRate),
      hint: "dos projetos encerrados"
    }];
    let t11;
    if ($[29] !== organizationSlug || $[30] !== subscriptionLocked) {
      t11 = path => path && organizationSlug && !subscriptionLocked ? buildOrganizationPath(organizationSlug, path) as Route : undefined;
      $[29] = organizationSlug;
      $[30] = subscriptionLocked;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    tileHref = t11;
    const t12 = isLoading || undefined;
    const t13 = stats.map(stat => <StatTile hint={stat.hint} href={tileHref(stat.path)} key={stat.label} label={stat.label} value={stat.value} />);
    if ($[32] !== t12 || $[33] !== t13) {
      t10 = <StatGrid aria-busy={t12}>{t13}</StatGrid>;
      $[32] = t12;
      $[33] = t13;
      $[34] = t10;
    } else {
      t10 = $[34];
    }
    T1 = Section;
    t9 = "Foco operacional";
    T0 = StatGrid;
    t7 = isLoading || undefined;
    t8 = focusItems.map(item => <StatTile hint={item.hint} href={tileHref(item.path)} key={item.label} label={item.label} value={item.value} />);
    $[10] = activeProducers;
    $[11] = canceledProjects;
    $[12] = completedProjects;
    $[13] = completionRate;
    $[14] = isLoading;
    $[15] = openProjects;
    $[16] = organizationSlug;
    $[17] = recentlyCreatedProducers;
    $[18] = show;
    $[19] = subscriptionLocked;
    $[20] = totalProducers;
    $[21] = totalProjects;
    $[22] = T0;
    $[23] = T1;
    $[24] = t10;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = tileHref;
  } else {
    T0 = $[22];
    T1 = $[23];
    t10 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
    tileHref = $[28];
  }
  let t11;
  if ($[35] !== T0 || $[36] !== t7 || $[37] !== t8) {
    t11 = <T0 aria-busy={t7}>{t8}</T0>;
    $[35] = T0;
    $[36] = t7;
    $[37] = t8;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== T1 || $[40] !== t11 || $[41] !== t9) {
    t12 = <T1 title={t9}>{t11}</T1>;
    $[39] = T1;
    $[40] = t11;
    $[41] = t9;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== isLoading || $[44] !== projectDashboard || $[45] !== tileHref) {
    t13 = isLoading ? <ChartSections monthly={<MonthlyProjectsChartSkeleton />} workflow={<OpenWorkflowChartSkeleton />} /> : projectDashboard ? <ChartSections monthly={<MonthlyProjectsChart months={projectDashboard.createdByMonth} />} workflow={<OpenWorkflowChart hrefFor={workflow => workflow === "unrecorded" ? undefined : tileHref(`/projects?status=open&workflowStatus=${workflow}`)} rows={projectDashboard.openByWorkflow} />} /> : null;
    $[43] = isLoading;
    $[44] = projectDashboard;
    $[45] = tileHref;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== t10 || $[48] !== t12 || $[49] !== t13) {
    t14 = <>{t10}{t12}{t13}</>;
    $[47] = t10;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
}